"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.LoaderGhostGroup = void 0;
require("./LoaderGhostGroup.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _LoaderGhostBase = _interopRequireDefault(require("../LoaderGhostBase/LoaderGhostBase"));
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * LoaderGhostGroup component
 *
 * @method
 * @param { LoaderGhostGroupProps } props
 * @returns LoaderGhostGroup
 */
const LoaderGhostGroup = props => {
  const {
    showTitle = defaultProps.showTitle,
    titleAlignment = defaultProps.titleAlignment,
    variant = defaultProps.variant,
    numberAcrossXl = defaultProps.numberAcrossXl,
    numberAcrossLg = defaultProps.numberAcrossLg,
    numberAcrossMd = defaultProps.numberAcrossMd,
    numberAcrossSm = defaultProps.numberAcrossSm,
    numberRowsXl = defaultProps.numberRowsXl,
    numberRowsLg = defaultProps.numberRowsLg,
    numberRowsMd = defaultProps.numberRowsMd,
    numberRowsSm = defaultProps.numberRowsSm,
    cardHeightXl = defaultProps.cardHeightXl,
    cardHeightLg = defaultProps.cardHeightLg,
    cardHeightMd = defaultProps.cardHeightMd,
    cardHeightSm = defaultProps.cardHeightSm,
    breakpoint = defaultProps.breakpoint,
    className,
    ...rest
  } = props;

  /**
   * Determine how many cards to render based on the requested breakpoint.
   * The breakpoint is provided by the consuming app (for example via an
   * inflection/provider layer), and we do not perform any window width
   * detection here.
   */
  const getCardsForBreakpoint = () => {
    const bp = breakpoint || 'SM';
    switch (bp) {
      case 'XL':
        return Math.max(Math.ceil(numberAcrossXl * numberRowsXl || 0), 1);
      case 'LG':
        return Math.max(Math.ceil(numberAcrossLg * numberRowsLg || 0), 1);
      case 'MD':
        return Math.max(Math.ceil(numberAcrossMd * numberRowsMd || 0), 1);
      case 'SM':
      default:
        return Math.max(Math.ceil(numberAcrossSm * numberRowsSm || 0), 1);
    }
  };
  const totalCards = getCardsForBreakpoint();
  const componentClassName = (0, _classnames.default)('pal-c-LoaderGhostGroup', {
    'pal-c-LoaderGhostGroup--leftAlign': titleAlignment === 'left',
    'pal-c-LoaderGhostGroup--centerAlign': titleAlignment === 'center',
    [`pal-c-LoaderGhostGroup--${variant}`]: !!variant
  }, className);
  const style = {
    '--pal-loaderghostgroup-cols-xl': numberAcrossXl || 1,
    '--pal-loaderghostgroup-cols-lg': numberAcrossLg || 1,
    '--pal-loaderghostgroup-cols-md': numberAcrossMd || 1,
    '--pal-loaderghostgroup-cols-sm': numberAcrossSm || 1,
    '--pal-loaderghostgroup-rows-xl': numberRowsXl,
    '--pal-loaderghostgroup-rows-lg': numberRowsLg,
    '--pal-loaderghostgroup-rows-md': numberRowsMd,
    '--pal-loaderghostgroup-rows-sm': numberRowsSm,
    '--pal-loaderghostgroup-card-height-xl': cardHeightXl,
    '--pal-loaderghostgroup-card-height-lg': cardHeightLg,
    '--pal-loaderghostgroup-card-height-md': cardHeightMd,
    '--pal-loaderghostgroup-card-height-sm': cardHeightSm
  };
  const cards = Array.from({
    length: totalCards
  }).map((_item, index) => /*#__PURE__*/_react.default.createElement(_LoaderGhostCard.default, {
    key: `loader-ghost-card-${index}`,
    variant: variant,
    className: "pal-c-LoaderGhostGroup__card"
  }));
  return /*#__PURE__*/_react.default.createElement("div", _extends({
    className: componentClassName,
    style: style,
    "aria-hidden": "true"
  }, rest), showTitle && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-LoaderGhostGroup__title"
  }, /*#__PURE__*/_react.default.createElement(_LoaderGhostBase.default, {
    variant: "pill"
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-LoaderGhostGroup__cardContainer"
  }, cards));
};

/**
 * Property type definitions
 *
 * @typedef LoaderGhostGroupProps
 * @type {object}
 * @property {boolean} showTitle - Controls whether a title skeleton row is shown above the cards.
 * @property {string} titleAlignment - Controls horizontal alignment of the title skeleton.
 * @property {string} variant - Controls the visual layout of each LoaderGhostCard.
 * @property {number} numberAcrossXl - Number of cards to show per row at the xl breakpoint.
 * @property {number} numberAcrossLg - Number of cards to show per row at the lg breakpoint.
 * @property {number} numberAcrossMd - Number of cards to show per row at the md breakpoint.
 * @property {number} numberAcrossSm - Density hint for small screens (can be fractional, e.g. 1.2).
 * @property {number} numberRowsXl - Number of rows to show at the xl breakpoint.
 * @property {number} numberRowsLg - Number of rows to show at the lg breakpoint.
 * @property {number} numberRowsMd - Number of rows to show at the md breakpoint.
 * @property {number} numberRowsSm - Number of rows to show at the sm breakpoint.
 * @property {string} breakpoint - Optional breakpoint key used to determine card count ("SM", "MD", "LG", "XL").
 * @property {string} cardHeightXl - Height of each card at the xl breakpoint (CSS length string, e.g. "32.2rem").
 * @property {string} cardHeightLg - Height of each card at the lg breakpoint (CSS length string, e.g. "32.2rem").
 * @property {string} cardHeightMd - Height of each card at the md breakpoint (CSS length string, e.g. "22.8rem").
 * @property {string} cardHeightSm - Height of each card at the sm breakpoint (CSS length string, e.g. "27.2rem").
 * @property {string} className - Optional additional class names for the root element.
 */
exports.LoaderGhostGroup = LoaderGhostGroup;
const propTypes = exports.propTypes = {
  /**
   * Controls whether a title skeleton row is shown above the cards.
   */
  showTitle: _propTypes.default.bool,
  /**
   * Controls horizontal alignment of the title skeleton.
   * - **left:** Title skeleton is left aligned.
   * - **center:** Title skeleton is centered.
   */
  titleAlignment: _propTypes.default.oneOf(['left', 'center']),
  /**
   * Controls the visual layout of each LoaderGhostCard.
   * - **default:** Media block with basic text skeleton.
   * - **textRectangle:** Media block with two text lines.
   * - **textCircle:** Media block with a single, shorter text line.
   */
  variant: _propTypes.default.oneOf(['default', 'textRectangle', 'textCircle']),
  /**
   * Number of cards to show per row at the xl breakpoint.
   */
  numberAcrossXl: _propTypes.default.number,
  /**
   * Number of cards to show per row at the lg breakpoint.
   */
  numberAcrossLg: _propTypes.default.number,
  /**
   * Number of cards to show per row at the md breakpoint.
   */
  numberAcrossMd: _propTypes.default.number,
  /**
   * Density hint for small screens. Can be fractional (e.g. **1.2**)
   * to mirror Swiper rails, and is used to determine total card count.
   */
  numberAcrossSm: _propTypes.default.number,
  /**
   * Number of rows to show at the xl breakpoint.
   */
  numberRowsXl: _propTypes.default.number,
  /**
   * Number of rows to show at the lg breakpoint.
   */
  numberRowsLg: _propTypes.default.number,
  /**
   * Number of rows to show at the md breakpoint.
   */
  numberRowsMd: _propTypes.default.number,
  /**
   * Number of rows to show at the sm breakpoint.
   */
  numberRowsSm: _propTypes.default.number,
  /**
   * Optional breakpoint key used to determine card count.
   * This value is expected to be provided by the consuming application
   * (for example via an inflection/provider layer), not computed here.
   * - **SM:** small breakpoint configuration
   * - **MD:** medium breakpoint configuration
   * - **LG:** large breakpoint configuration
   * - **XL:** extra-large breakpoint configuration
   */
  breakpoint: _propTypes.default.string,
  /**
   * Height of each card at the xl breakpoint.
   * Expect a CSS length string (for example: **"32.2rem"**).
   */
  cardHeightXl: _propTypes.default.string,
  /**
   * Height of each card at the lg breakpoint.
   * Expect a CSS length string (for example: **"32.2rem"**).
   */
  cardHeightLg: _propTypes.default.string,
  /**
   * Height of each card at the md breakpoint.
   * Expect a CSS length string (for example: **"22.8rem"**).
   */
  cardHeightMd: _propTypes.default.string,
  /**
   * Height of each card at the sm breakpoint.
   * Expect a CSS length string (for example: **"27.2rem"**).
   */
  cardHeightSm: _propTypes.default.string,
  /**
   * Optional additional class names for the root element.
   */
  className: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} showTitle=true - Shows the title skeleton row by default.
 * @property {string} titleAlignment='left' - Left aligns the title skeleton by default.
 * @property {string} variant='default' - Uses the default LoaderGhostCard variant by default.
 * @property {number} numberAcrossXl=4 - Defaults to 4 cards across on xl.
 * @property {number} numberAcrossLg=4 - Defaults to 4 cards across on lg.
 * @property {number} numberAcrossMd=3 - Defaults to 3 cards across on md.
 * @property {number} numberAcrossSm=1.2 - Defaults to 1.2 density on small (for Swiper-style usage).
 * @property {number} numberRowsXl=1 - Defaults to 1 row on xl.
 * @property {number} numberRowsLg=1 - Defaults to 1 row on lg.
 * @property {number} numberRowsMd=1 - Defaults to 1 row on md.
 * @property {number} numberRowsSm=1 - Defaults to 1 row on sm.
 * @property {string} cardHeightXl='32.2rem' - Default xl min-height from Figma.
 * @property {string} cardHeightLg='32.2rem' - Default lg min-height from Figma.
 * @property {string} cardHeightMd='22.8rem' - Default md min-height from Figma.
 * @property {string} cardHeightSm='27.2rem' - Default sm min-height from Figma.
 * @property {string} breakpoint='SM' - Default breakpoint configuration used for card count.
 */
const defaultProps = exports.defaultProps = {
  showTitle: true,
  titleAlignment: 'left',
  variant: 'default',
  numberAcrossXl: 4,
  numberAcrossLg: 4,
  numberAcrossMd: 3,
  numberAcrossSm: 2,
  numberRowsXl: 3,
  numberRowsLg: 3,
  numberRowsMd: 3,
  numberRowsSm: 3,
  cardHeightXl: '27.3333rem',
  cardHeightLg: '27.3333rem',
  cardHeightMd: '27.3333rem',
  cardHeightSm: '27.3333rem',
  breakpoint: 'SM'
};
LoaderGhostGroup.propTypes = propTypes;
LoaderGhostGroup.defaultProps = defaultProps;
var _default = exports.default = LoaderGhostGroup;